import { createApiClient } from './apiClient';

import { SurveyParticipant } from '../../types/survey';
import { 
  CreateSurveyRequest, 
  SurveyIndicatorsResponse,
  Survey,
  SurveyApiResponse,
  SurveyNotificationData,
  SurveySubmissionData,
  SurveySearchResponse,
  SurveyListResponse,
  SurveyParticipantsResponse,
  ParentAccountLinksResponse,
  NotificationHistoryResponse,
  IndividualParticipantNotificationPayload,
  IndividualParentNotificationPayload,
  BulkNotificationPayload,
  ConsentTemplate,
  ConsentRequestResponse,
  ConsentSendInclude,
  BulkAddParticipantsResponse
} from '../../types/survey';

import { SearchSurveysParams, SearchParticipantSurveysParams } from '../../types/search-params';

interface ArchiveSurveyRequest {
  surveyIds: string[];
}

interface UnarchiveSurveyRequest {
  surveyIds: string[];
}



// Survey API Service
export const surveyService = {
  // Get all survey indicators
  getSurveyIndicators: async (v2: boolean = true, includeQuestions: boolean = false, owner?: string): Promise<SurveyIndicatorsResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/survey/indicators', {
      params: { 
        v2,
        questions: includeQuestions,
        ...(owner && { owner })
      }
    });
    return response.data;
  },

  // Create a new survey
  createSurvey: async (surveyData: CreateSurveyRequest): Promise<SurveyApiResponse<Survey>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/survey', surveyData);
    return response.data;
  },

  // Get survey by ID
  getSurveyById: async (surveyId: string, limit?: number, offset?: number): Promise<Survey> => {
    const apiClient = createApiClient();
    const params: Record<string, number> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    const response = await apiClient.get(`/survey/${surveyId}`, { params });
    return response.data;
  },

  // Update an existing survey
  updateSurvey: async (surveyData: CreateSurveyRequest): Promise<SurveyApiResponse<Survey>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/survey', surveyData);
    return response.data;
  },

  // Update survey attributes (consent template, etc.)
  updateSurveyAttributes: async (surveyId: string, attributes: { consentType?: string; consentTemplate?: ConsentTemplate; [key: string]: unknown }): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const payload = {
      surveys: [
        {
          id: surveyId,
          ...attributes
        }
      ]
    };
    const response = await apiClient.post('/surveys', payload);
    return response.data;
  },

  // Delete a survey
  deleteSurvey: async (surveyId: string): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.delete(`/survey/${surveyId}`);
    return response.data;
  },

  // Get survey notification data
  getSurveyNotificationData: async (surveyId: string): Promise<SurveyNotificationData> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/survey/${surveyId}/notificationData`);
    return response.data;
  },

  // Get survey responses for a participant
  getSurveyResponses: async (participantId: string): Promise<SurveySubmissionData> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/survey/responses/${participantId}`);
    return response.data;
  },

  // Close a survey
  closeSurvey: async (surveyId: string): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/closeSurvey/${surveyId}`);
    return response.data;
  },

  // Notify a survey participant
  notifyParticipant: async (notificationData: Record<string, unknown>): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/survey/notifications/notify-participant', notificationData);
    return response.data;
  },

  // Verify a parent
  verifyParent: async (verificationData: Record<string, unknown>): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/participant/verify-parent', verificationData);
    return response.data;
  },

  // Save participant responses
  saveParticipantResponses: async (responseData: Record<string, unknown>): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/participant/save', responseData);
    return response.data;
  },

  // Send survey email
  sendSurveyEmail: async (emailData: Record<string, unknown>): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/survey/sendemail', emailData);
    return response.data;
  },

  // Search surveys by district or school
  searchSurveys: async (params: SearchSurveysParams): Promise<SurveySearchResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/surveys', { params });
    return response.data;
  },

  // Search surveys by participant
  searchParticipantSurveys: async (params: SearchParticipantSurveysParams): Promise<SurveyListResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/surveys/participant', { params });
    return response.data;
  },

  // Bulk add participants to a survey
  bulkAddParticipants: async (surveyId: string, participants: SurveyParticipant[]): Promise<BulkAddParticipantsResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.post(`/bulkAddParticipants/${surveyId}`, {
      participants
    });
    return response.data;
  },

  // Archive surveys
  archiveSurveys: async (archiveData: ArchiveSurveyRequest): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/surveys', { 
      surveys: archiveData.surveyIds.map(id => ({ 
        id, 
        isArchived: true 
      }))
    });
    return response.data;
  },

  // Unarchive surveys
  unarchiveSurveys: async (unarchiveData: UnarchiveSurveyRequest): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/surveys', { 
      surveys: unarchiveData.surveyIds.map(id => ({ 
        id, 
        isArchived: false 
      }))
    });
    return response.data;
  },

  // Bulk invite participants to a survey
  bulkInviteParticipants: async (surveyId: string, participantIds: string[]): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const payload: BulkNotificationPayload = {
      participantIds,
      surveyId
    };
    const response = await apiClient.post(`/survey/notifications/bulk-notify/${surveyId}`, payload);
    return response.data;
  },

  // Get notification data for raw data download
  getNotificationData: async (surveyIds: string[]): Promise<SurveyApiResponse<SurveyNotificationData[]>> => {
    const apiClient = createApiClient();
    const response = await apiClient.post('/survey/notificationData', {
      surveyIds
    });
    return response.data;
  },

  // Send consent request for a survey (bulk)
  sendConsentRequest: async (
    surveyId: string,
    options: { include?: ConsentSendInclude[]; dryRun?: boolean } = {}
  ): Promise<ConsentRequestResponse> => {
    const apiClient = createApiClient();
    const params: Record<string, string> = {};
    const include = options.include?.length ? options.include : (['new', 'updated'] as ConsentSendInclude[]);
    params.include = include.join(',');
    if (options.dryRun) {
      params.dryRun = 'true';
    }
    const response = await apiClient.get(`/v2/survey/notifications/send-consent/${surveyId}`, {
      params,
    });
    return response.data;
  },

  // Send consent request to individual participant
  sendIndividualConsentRequest: async (surveyId: string, participantId: string): Promise<ConsentRequestResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/v2/survey/notifications/send-consent/${surveyId}?participantId=${participantId}`);
    return response.data;
  },

  // Get participants for a survey
  getSurveyParticipants: async (surveyId: string): Promise<SurveyParticipantsResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/survey/${surveyId}/participants`);
    return response.data;
  },

  // List B2C parent account links for every participant on a survey (tm_admin)
  getSurveyParentAccountLinks: async (surveyId: string): Promise<ParentAccountLinksResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/survey/${surveyId}/parentAccountLinks`);
    return response.data;
  },

  // Get notification history for a participant
  getNotificationHistory: async (surveyId: string, participantId: string): Promise<NotificationHistoryResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get(`/survey/notifications/log/${surveyId}/${participantId}`);
    return response.data;
  },

  // Notify participant for individual invite
  notifyIndividualParticipant: async (participant: SurveyParticipant, surveyId: string): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const payload: IndividualParticipantNotificationPayload = {
      participant: {
        participant,
        surveyId,
      },
      surveyId,
    };
    const response = await apiClient.post('/survey/notifications/notify-participant', payload);
    return response.data;
  },

  // Notify parent for a single participant
  notifyParent: async (participant: SurveyParticipant, surveyId: string): Promise<SurveyApiResponse<void>> => {
    const apiClient = createApiClient();
    const payload: IndividualParentNotificationPayload = {
      participant: {
        participant,
        surveyId,
      },
      surveyId,
    };
    const response = await apiClient.post('/survey/notifications/notify-parent', payload);
    return response.data;
  }
}; 